import { createApp } from 'vue';
import { createPinia } from 'pinia';
import ElementPlus from 'element-plus';
import 'element-plus/dist/index.css';
import * as ElementPlusIconsVue from '@element-plus/icons-vue';
import { createKsVue } from '@siclop/kstone-vue';
import { restoreAdminAuth, clearAdminAuth } from '@siclop/ksadmin-vue';

// Bibliothèques Siclop : styles + plugin Vue (active useApiClient,
// useSignalR, useAuth, signalRStoreManager — cf composables/useTheHoSignalR).
import '@siclop/kstone-vue/dist/style.css';
import '@siclop/ksadmin-vue/dist/style.css';

// Theme global THE HO! (apres Element Plus pour pouvoir l'overrider)
import '@/assets/styles/main.scss';

import App from './App.vue';
import AppTooltip from '@/components/shared/AppTooltip.vue';
import AppInfoCard from '@/components/shared/AppInfoCard.vue';
import DialogActions from '@/components/shared/DialogActions.vue';
import MessageBody from '@/components/shared/MessageBody.vue';
import ItemDisplay from '@/components/items/ItemDisplay.vue';
import { vImgHideOnError } from '@/directives/imgHideOnError';
import router from './router';
import { i18n } from './i18n';

const app = createApp(App);

// Element Plus + icônes
app.use(ElementPlus);
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component);
}

// Primitives tooltip maison — enregistrées globalement pour usage sans import
// (cf composants/shared/AppTooltip + AppInfoCard, langage visuel _tooltips.scss)
app.component('AppTooltip', AppTooltip);
app.component('AppInfoCard', AppInfoCard);
app.component('DialogActions', DialogActions);
// Rendu des corps de message avec icônes inline (cf utils/messageMarkup).
app.component('MessageBody', MessageBody);
// Affichage unifié d'objet (cf components/items/ItemDisplay) — enregistré
// globalement pour usage sans import dans toutes les surfaces d'items.
app.component('ItemDisplay', ItemDisplay);
// Directive globale : masque une <img> dont le chargement échoue (cf 23 vues).
app.directive('img-hide-on-error', vImgHideOnError);

app.use(createPinia());
app.use(router);
app.use(i18n);

// Auth = celle du PACKAGE (@siclop/ksadmin-vue), comme grimoire. On ré-hydrate
// l'état (user/resources/token) depuis localStorage AVANT de monter l'app : un
// F5 garde la session (le refresh silencieux du provider JWT renouvelle le token
// expiré au 1er appel). Ne purge que si le refresh token est mort / storage cassé.
try {
  restoreAdminAuth();
} catch {
  ['user', 'jwtToken', 'refreshToken', 'refreshTokenExpiryTime', 'expiration', 'connexion', 'resources']
    .forEach((k) => localStorage.removeItem(k));
}

// Plugin KStone : provider JWT + useApiClient + useSignalR. On laisse les clés
// localStorage par DÉFAUT (jwtToken / refreshToken / refreshTokenExpiryTime) :
// ce sont celles, EN DUR, qu'écrivent useAdminAuth/restoreAdminAuth. Les forcer
// (ancien `theho.auth.token`) désynchronisait le bridge -> session non restaurée.
app.use(
  createKsVue({
    baseUrl: import.meta.env.VITE_API_BASE_URL ?? '',
    auth: {
      type: 'jwt',
      // Routes RÉELLES du backend KSAdmin (cf api/auth.ts).
      loginEndpoint: '/authentication_token',
      refreshEndpoint: '/refresh-token',
      onLogout: () => {
        // Refresh réactif réellement invalidé (401/403) : purge complète via le
        // package (les 7 clés) pour ne pas laisser de session fantôme.
        clearAdminAuth();
        router.push({ name: 'login' });
      },
    },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as any,
);

app.mount('#app');
